import React, { useState, useEffect } from 'react'
import { Card, CardContent, CardHeader } from '../components/ui'
import { useI18n } from '../i18n'
import { useAuth } from '../components/AuthProvider'
import { API_ENDPOINTS } from '../config/api'
import { 
  User, 
  Mail, 
  Shield, 
  Calendar,
  Clock,
  Settings,
  Edit,
  Save,
  X
} from 'lucide-react'
import { pageScope } from '../lib/cssScope'
import { Button } from '../components/ui/Button'

interface UserProfile {
  id: string
  username: string
  email: string
  role: string
  permissions: string[]
  organizations?: string[]
  phone?: string
  position?: string
  createdAt: string
  lastLogin: string
}

export default function UserDashboard() {
  const { t } = useI18n()
  const { user: currentUser, token } = useAuth()
  const [profile, setProfile] = useState<UserProfile | null>(null)
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState(false)
  const [formData, setFormData] = useState({
    email: '',
    currentPassword: '',
    password: '',
    confirmPassword: ''
  })
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  useEffect(() => {
    if (currentUser) {
      const orgsRaw = Array.isArray(currentUser.organizations) ? currentUser.organizations : []
      const orgNames = orgsRaw
        .map((o: any) => (o && typeof o === 'object' && o.name ? String(o.name) : (o ? String(o) : '')))
        .filter((s: string) => s.length > 0)
      setProfile({
        id: currentUser.id,
        username: currentUser.username,
        email: currentUser.email,
        role: currentUser.role,
        permissions: currentUser.permissions,
        organizations: orgNames,
        phone: currentUser.phone,
        position: currentUser.position,
        createdAt: new Date().toISOString(),
        lastLogin: new Date().toISOString()
      })
      setFormData({ email: currentUser.email, currentPassword: '', password: '', confirmPassword: '' })
      setLoading(false)
    }
  }, [currentUser])

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setSuccess('')

    if (formData.password && formData.password !== formData.confirmPassword) {
      setError(t('auth.passwordsDontMatch'))
      return
    }

    try {
      // 1) Update email/profile
      const profileRes = await fetch(`/api/user-settings/profile`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email: formData.email })
      })
      if (!profileRes.ok) {
        const errorData = await profileRes.json().catch(()=>({}))
        throw new Error(errorData.message || t('user.updateProfileFailed'))
      }

      // 2) Change password if provided
      if (formData.password) {
        const pwdRes = await fetch(`/api/user-settings/password`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            currentPassword: formData.currentPassword || '',
            newPassword: formData.password,
            confirmPassword: formData.confirmPassword
          })
        })
        if (!pwdRes.ok) {
          const err = await pwdRes.json().catch(()=>({}))
          throw new Error(err.message || t('user.changePasswordFailed'))
        }
      }

      setSuccess(t('user.profileUpdated'))
      setEditing(false)
      setFormData(prev => ({ ...prev, currentPassword: '', password: '', confirmPassword: '' }))
    } catch (err) {
      setError(err instanceof Error ? err.message : t('common.error'))
    }
  }

  const handleCancel = () => {
    setEditing(false)
    setFormData({ 
      email: profile?.email || '', 
      currentPassword: '',
      password: '', 
      confirmPassword: '' 
    })
    setError('')
    setSuccess('')
  }

  if (loading) {
    return (
      <div className={`${pageScope('user-dashboard')} flex items-center justify-center min-h-96`}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">{t('common.loading')}</p>
        </div>
      </div>
    )
  }

  if (!profile) {
    return (
      <div className={`${pageScope('user-dashboard')} flex items-center justify-center min-h-96`}>
        <div className="text-center">
          <User className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">{t('user.profileNotFound')}</h2>
          <p className="text-gray-600">{t('user.unableLoad')}</p>
        </div>
      </div>
    )
  }

  return (
    <div className={`${pageScope('user-dashboard')} space-y-6`}>
      <div className="flex justify-between items-center">
        <Button
          type="button"
          color="primary"
          onClick={() => setEditing(!editing)}
          className="btn-primary inline-flex items-center gap-2 px-4 py-2 rounded-md"
          icon={editing ? <X className="h-4 w-4" /> : <Edit className="h-4 w-4" />}
        >
          {editing ? t('common.cancel') : t('user.editProfile')}
        </Button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <X className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">{t('common.error')}</h3>
              <div className="mt-2 text-sm text-red-700">{error}</div>
            </div>
          </div>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 rounded-md p-4">
          <div className="flex">
            <Save className="h-5 w-5 text-green-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-green-800">{t('common.success')}</h3>
              <div className="mt-2 text-sm text-green-700">{success}</div>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Profile Information */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader className="p-4 pb-0">
              <h3 className="text-lg font-semibold">{t('user.profileInfo')}</h3>
            </CardHeader>
            <CardContent className="p-4">
              {editing ? (
                <form onSubmit={handleSave} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Username
                    </label>
                    <input
                      type="text"
                      value={profile.username}
                      disabled
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">{t('user.usernameImmutable')}</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('auth.email')}</label>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('user.organization')}</label>
                    <input
                      type="text"
                      value={(Array.isArray(profile.organizations) && profile.organizations.length > 0)
                        ? profile.organizations[0]
                        : (((currentUser as any)?.organization || (currentUser as any)?.organizationName) || '')}
                      disabled
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-700"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('user.currentPassword')}</label>
                    <input
                      type="password"
                      autoComplete="new-password"
                      value={formData.currentPassword}
                      onChange={(e) => setFormData(prev => ({ ...prev, currentPassword: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('user.newPasswordOptional')}</label>
                    <input
                      type="password"
                      autoComplete="new-password"
                      value={formData.password}
                      onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  {formData.password && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('user.confirmNewPassword')}</label>
                      <input
                        type="password"
                        autoComplete="new-password"
                        value={formData.confirmPassword}
                        onChange={(e) => setFormData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  )}

                  <div className="flex justify-end space-x-3 pt-4">
                    <Button type="button" color="secondary" onClick={handleCancel} className="px-4 py-2 text-sm font-medium text-gray-700">
                      {t('common.cancel')}
                    </Button>
                    <Button type="submit" color="primary" className="px-4 py-2 text-sm font-medium">
                      {t('user.saveChanges')}
                    </Button>
                  </div>
                </form>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center space-x-3">
                    <User className="h-5 w-5 text-gray-400" />
                    <div>
                      <p className="text-sm font-medium text-gray-500">Username</p>
                      <p className="text-lg text-gray-900">{profile.username}</p>
                    </div>
                  </div>

                  <div className="flex items-center space-x-3">
                    <Mail className="h-5 w-5 text-gray-400" />
                    <div>
                      <p className="text-sm font-medium text-gray-500">Email</p>
                      <p className="text-lg text-gray-900">{profile.email}</p>
                    </div>
                  </div>

                  <div className="flex items-center space-x-3">
                    <Shield className="h-5 w-5 text-gray-400" />
                    <div>
                      <p className="text-sm font-medium text-gray-500">{t('user.organization')}</p>
                      <p className="text-lg text-gray-900">{(Array.isArray(profile.organizations) && profile.organizations.length > 0) ? profile.organizations[0] : (((currentUser as any)?.organization || (currentUser as any)?.organizationName) || '—')}</p>
                    </div>
                  </div>

                  <div className="flex items-center space-x-3">
                    <Shield className="h-5 w-5 text-gray-400" />
                    <div>
                      <p className="text-sm font-medium text-gray-500">Role</p>
                      <p className="text-lg text-gray-900 capitalize">{profile.role}</p>
                    </div>
                  </div>

                  {profile.organizations && profile.organizations.length > 0 && (
                    <div className="flex items-center space-x-3">
                      <Shield className="h-5 w-5 text-gray-400" />
                      <div>
                        <p className="text-sm font-medium text-gray-500">Organizations</p>
                        <p className="text-lg text-gray-900">{profile.organizations.join(', ')}</p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Account Details */}
        <div>
          <Card>
            <CardHeader className="p-4 pb-0">
              <h3 className="text-lg font-semibold">{t('user.accountDetails')}</h3>
            </CardHeader>
            <CardContent className="p-4">
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <Calendar className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="text-sm font-medium text-gray-500">{t('user.memberSince')}</p>
                    <p className="text-sm text-gray-900">
                      {new Date(profile.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <Clock className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="text-sm font-medium text-gray-500">{t('user.lastLogin')}</p>
                    <p className="text-sm text-gray-900">
                      {new Date(profile.lastLogin).toLocaleDateString()}
                    </p>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <Settings className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="text-sm font-medium text-gray-500">{t('user.permissions')}</p>
                    <p className="text-sm text-gray-900">
                      {profile.permissions.length} granted
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

        </div>
      </div>
    </div>
  )
}